import { useTranslations } from 'next-intl'
import { z } from 'zod'

import { TaxReceiptType, ZReceiptInformation } from '@/entities/user'
import { isNilOrEmptyString } from '@/shared/lib'

export const RECEIPT_INFORMATION_FIELDS = [
  'receiptRequested',
  'taxReceiptType',
  'personalIssuanceNumber',
  'businessIssuanceNumber',
  'corpId',
  'corpName',
  'corpCeo',
  'invoiceeEmail',
]

export const ZReceiptInformationForm = ZReceiptInformation.pick({
  taxReceiptType: true,
  corpId: true,
  corpName: true,
  corpCeo: true,
  invoiceeEmail: true,
}).extend({
  receiptRequested: z.enum(['true', 'false']).nullable(),
  personalIssuanceNumber: z.string().nullable(),
  businessIssuanceNumber: z.string().nullable(),
})

export type TReceiptInformationForm = z.infer<typeof ZReceiptInformationForm>

export const createReceiptInformationValidation =
  (t: ReturnType<typeof useTranslations<'receipt_information'>>) =>
  (data: TReceiptInformationForm, ctx: z.RefinementCtx) => {
    if (data.receiptRequested === null) {
      ctx.addIssue({
        path: ['receiptRequested'],
        code: z.ZodIssueCode.custom,
        message: t('error.receipt_requested_required'),
      })
    }

    if (data.receiptRequested === 'false') {
      return
    }

    if (data.taxReceiptType === TaxReceiptType.CASH_RECEIPT_PERSONAL) {
      if (isNilOrEmptyString(data.personalIssuanceNumber)) {
        ctx.addIssue({
          path: ['personalIssuanceNumber'],
          code: z.ZodIssueCode.custom,
          message: t('error.personal_issuance_number_required'),
        })
      }
    }

    if (data.taxReceiptType === TaxReceiptType.CASH_RECEIPT_BUSINESS) {
      if (isNilOrEmptyString(data.businessIssuanceNumber)) {
        ctx.addIssue({
          path: ['businessIssuanceNumber'],
          code: z.ZodIssueCode.custom,
          message: t('error.business_issuance_number_required'),
        })
      }
    }

    if (data.taxReceiptType === TaxReceiptType.TAX_INVOICE) {
      if (isNilOrEmptyString(data.corpId)) {
        ctx.addIssue({
          path: ['corpId'],
          code: z.ZodIssueCode.custom,
          message: t('error.corp_id_required'),
        })
      }
      if (isNilOrEmptyString(data.corpName)) {
        ctx.addIssue({
          path: ['corpName'],
          code: z.ZodIssueCode.custom,
          message: t('error.corp_name_required'),
        })
      }
      if (isNilOrEmptyString(data.corpCeo)) {
        ctx.addIssue({
          path: ['corpCeo'],
          code: z.ZodIssueCode.custom,
          message: t('error.corp_ceo_required'),
        })
      }
    }
  }

export const receiptInformationDefaultValues = {
  receiptRequested: null,
  taxReceiptType: null,
  personalIssuanceNumber: '',
  businessIssuanceNumber: '',
  corpId: '',
  corpName: '',
  corpCeo: '',
  invoiceeEmail: '',
}
